// src/lib/pageMeta.js
//
// Per-page <title> and meta description, keyed by the SAME keys as slugMap in
// ./slugs.js. Keeping one key space means a page's URL and its metadata can
// never drift apart, and a typo shows up as a missing entry rather than as a
// wrong tag on a live page.
//
// Only pages that need custom wording belong here. Anything missing falls back
// to SITE_NAME, which is what the whole site did before — so an incomplete
// file is never worse than the current state.
//
// TODO: populate from the marketing metadata sheet. Format per entry:
//
//   who_we_are: {
//     en: { title: '…', description: '…' },
//     es: { title: '…', description: '…' },
//   },
//
// Guidance for whoever fills this in:
//   - title:       50-60 characters. Google truncates past ~60.
//   - description: 150-160 characters. Not a ranking factor, but it is the
//                  sentence people read in the results, so it decides clicks.
//   - Both must be unique per page. Duplicated descriptions across pages are
//     worse than none at all — Google flags them and usually writes its own.

/** Appended to every title, so pages do not have to repeat it. */
export const SITE_NAME = 'HANSA-FLEX';

/**
 * Preview picture used when a link is shared on LinkedIn, WhatsApp, Teams or
 * Facebook. Generated by scripts/generate-og-image.js — one per language,
 * because the strapline is visible in the card.
 *
 * Any page can override it by adding an `image` to its entry below; the value
 * is a path from the site root, e.g. '/banner-innotrans-es.jpg'. Give a real
 * photograph where one exists — a product shot always beats a logo.
 */
export const DEFAULT_OG_IMAGE = {
  en: '/og-image-en.jpg',
  es: '/og-image.jpg',
};

/** Used when a page has no entry below. */
export const DEFAULT_META = {
  en: {
    title: 'HANSA-FLEX Spain — Hydraulic and pneumatic solutions',
    description:
      'HANSA-FLEX Ibérica supplies hoses, connection technology and hydraulic services across Spain, backed by a Europe-wide network and 24-hour availability.',
  },
  es: {
    title: 'HANSA-FLEX España — Soluciones hidráulicas y neumáticas',
    description:
      'HANSA-FLEX Ibérica suministra mangueras, técnica de conexión y servicios hidráulicos en toda España, con una red europea y disponibilidad 24 horas.',
  },
};

export const pageMeta = {
  who_we_are: {
    en: {
      title: 'HANSA-FLEX Spain | Hydraulic solutions for industry',
      description: 'HANSA-FLEX IBÉRICA: decades of hydraulics expertise, Pneumatic systems, global reach, mobile service, X-CODE hose management and a full product & service range for Spanish industry.',
    },
    es: {
      title: 'HANSA-FLEX España | Productos, servicios y soluciones para hidráulica y neumática',
      description: 'HANSA-FLEX IBÉRICA: multinacional especializada en hidráulica y neumática industrial, con décadas de experiencia, servicio móvil rápido, X-CODE y una amplia gama de productos y servicios en España.',
    },
  },
  certificates: {
    en: {
      title: 'HANSA-FLEX | Certificates – ISO 9001, ISO 14001, ISO 45001 & creditworthiness',
      description: 'HANSA-FLEX holds EN ISO 9001:2015 quality management, DIN EN ISO 14001:2015 environmental management, DIN ISO 45001 occupational health and safety certification, and strong Creditreform creditworthiness ratings. Download certificates and learn what they mean for your projects.',
    },
    es: {
      title: 'HANSA-FLEX | Certificados – ISO 9001, ISO 14001, ISO 45001 y solvencia',
      description: 'HANSA-FLEX cuenta con las certificaciones de gestión de calidad EN ISO 9001:2015, de gestión medioambiental DIN EN ISO 14001:2015, de salud y seguridad en el trabajo DIN ISO 45001, y con sólidas calificaciones de solvencia de Creditreform. Descargue los certificados y descubra lo que significan para sus proyectos.',
    },
  },
  contact_us: {
    en: {
      title: 'Contact HANSA-FLEX Spain | Hydraulic Support & Quotes',
      description: 'Contact HANSA-FLEX Spain for hydraulic technical support, quotes and commercial enquiries. Reach us by phone, WhatsApp, email or contact form.',
    },
    es: {
      title: 'Contacto HANSA-FLEX España | Soporte técnico y presupuestos',
      description: 'Contacta con HANSA-FLEX España para soporte técnico, presupuestos y consultas de hidráulica industrial. Atención por teléfono, WhatsApp, email o formulario.',
    },
  },
  railway: {
    en: {
      title: 'Railway Hydraulic Solutions | HANSA-FLEX Spain',
      description: 'HANSA-FLEX Spain supplies certified hydraulic hoses and components for railway applications, compliant with EN 45545-2 and EN 15085-2 (CL1)',
    },
    es: {
      title: 'Soluciones hidráulicas ferroviarias | HANSA-FLEX España',
      description: 'HANSA-FLEX España suministra mangueras y componentes hidráulicos certificados para aplicaciones ferroviarias, con cumplimiento EN 45545-2 y EN 15085-2 (CL1)',
    },
  },
  hf_group_management: {
    en: {
      title: 'HANSA-FLEX Group Leadership | Family-run hydraulics',
      description: 'Meet the HANSA-FLEX Group leadership: family-owned and managed with clear values and global reach in fluid technology.',
    },
    es: {
      title: 'Dirección HANSA-FLEX | Grupo hidráulico familiar',
      description: 'Conoce el equipo directivo de HANSA-FLEX: empresa familiar e independiente, con valores sólidos y presencia mundial en hidráulica y neumática.',
    },
  },
  hf_group_history: {
    en: {
      title: 'HANSA-FLEX history | Customer-focused since 1962',
      description: 'Explore the HANSA-FLEX story: from its foundation in Bremen in 1962 to a global hydraulics group with 4,500+ employees in 40+ countries.',
    },
    es: {
      title: 'Historia de HANSA-FLEX |Compromiso con el cliente desde 1962',
      description: 'Conoce la historia de HANSA-FLEX: desde su fundación en Bremen en 1962 hasta un grupo hidráulico líder con 4.500+ empleados en 40+ países.',
    },
  },
  hf_60_years: {
    en: {
      title: '60 Years of HANSA-FLEX | Passion for hydraulics since 1962',
      description: 'Celebrating 60 years of HANSA-FLEX: from a local hose supplier in Bremen to a global hydraulics partner present in 40+ countries.',
    },
    es: {
      title: '60 Años de HANSA-FLEX | Pasión por la hidráulica',
      description: 'Celebramos 60 años de HANSA-FLEX: de proveedor local de mangueras a socio global de hidráulica presente en más de 40 países.',
    },
  },
  hf_group_mission: {
    en: {
      title: 'HANSA-FLEX mission & values | Code of Conduct',
      description: 'The HANSA-FLEX mission: togetherness, responsibility, respect and enthusiasm. Values guiding 4,500+ employees worldwide every day.',
    },
    es: {
      title: 'Misión HANSA-FLEX | Valores y código de conducta',
      description: 'Descubre la misión de HANSA-FLEX: unión, responsabilidad, respeto y entusiasmo. Valores que guían a 4.500+ empleados en todo el mundo.',
    },
  },
  responsibility_social: {
    en: {
      title: 'Corporate social responsibility | HANSA-FLEX',
      description: 'HANSA-FLEX supports communities through social commitment: education, sport and charitable donations. Learn about our CSR initiatives and foundation.',
    },
    es: {
      title: 'Responsabilidad social corporativa | HANSA-FLEX',
      description: 'HANSA-FLEX apoya a las comunidades con compromiso social: educación, deporte y donaciones solidarias. Conoce nuestra RSC y labor de la fundación.',
    },
  },
  donation_instead_gifts: {
    en: {
      title: 'HANSA-FLEX corporate donations | Instead of gifts',
      description: 'HANSA-FLEX donates to selected aid projects instead of promotional gifts. Discover the projects we support and our social contribution.',
    },
    es: {
      title: 'HANSA-FLEX: donaciones corporativas con impacto social',
      description: 'HANSA-FLEX dona a proyectos solidarios en lugar de regalos promocionales. Descubre los proyectos que apoyamos y nuestra contribución social.',
    },
  },
  diversity: {
    en: {
      title: 'Diversity & Inclusion | HANSA-FLEX values',
      description: 'HANSA-FLEX embraces diversity and inclusion as core values. Equitable, multicultural teams deliver better results for our clients worldwide.',
    },
    es: {
      title: 'Diversidad e Inclusión | HANSA-FLEX España',
      description: 'HANSA-FLEX defiende la diversidad y la inclusión como valores clave. Equipos multiculturales y entornos equitativos para mejores resultados.',
    },
  },
  hf_group_certificates: {
    en: {
      title: 'HANSA-FLEX Group certificates | ISO & quality ratings',
      description: 'View HANSA-FLEX Group certificates: ISO 9001, ISO 14001, ISO 45001 quality standards and Creditreform top creditworthiness rating.',
    },
    es: {
      title: 'Certificados grupo HANSA-FLEX | Normas ISO y solvencia',
      description: 'Consulta los certificados del grupo HANSA-FLEX: normas ISO 9001, ISO 14001, ISO 45001 y máxima calificación de solvencia Creditreform.',
    },
  },
  annual_report: {
    en: {
      title: 'HANSA-FLEX annual report | Key figures & highlights',
      description: 'View HANSA-FLEX key figures: global turnover, branches, employees and business highlights. Transparent reporting from a leading hydraulics group.',
    },
    es: {
      title: 'Informe anual HANSA-FLEX | Cifras clave y resultados',
      description: 'Consulta las cifras clave de HANSA-FLEX: facturación global, sedes, empleados y resultados. Transparencia de un grupo hidráulico líder.',
    },
  },
  associations: {
    en: {
      title: 'HANSA-FLEX industry associations | Hydraulic sector',
      description: 'HANSA-FLEX is active in hydraulic and industrial associations worldwide. Explore our memberships, standard-setting work and sector network.',
    },
    es: {
      title: 'Asociaciones del sector hidráulico | HANSA-FLEX',
      description: 'HANSA-FLEX participa en asociaciones del sector hidráulico e industrial. Conoce nuestras afiliaciones, trabajo en normativas y red sectorial.',
    },
  },
  hoses_and_hose_lines: {
    en: {
      title: 'Hydraulic & industrial hoses | HANSA-FLEX Spain',
      description: 'Full range of hydraulic, industrial, metal and PTFE hoses. HANSA-FLEX Spain: custom lengths, fast delivery and expert technical support.',
    },
    es: {
      title: 'Mangueras y líneas hidráulicas | HANSA-FLEX España',
      description: 'Gama completa de mangueras hidráulicas, industriales, metálicas y PTFE. HANSA-FLEX España: fabricación a medida, entrega rápida, soporte técnico.',
    },
  },
  hydraulic_hoses: {
    en: {
      title: 'Hydraulic hoses | HANSA-FLEX Spain – All applications',
      description: 'Hydraulic hoses for every application: elastomeric, thermoplastic and high-pressure hoses, with fast service across Spain.',
    },
    es: {
      title: 'Mangueras hidráulicas | HANSA-FLEX España',
      description: 'Mangueras hidráulicas para cada aplicación: elastoméricas, termoplásticas y de alta presión, con servicio rápido en toda España.',
    },
  },
  metal_hoses: {
    en: {
      title: 'Stainless steel metal hoses | HANSA-FLEX Spain',
      description: 'Metal hoses for extreme temperatures, high pressure and aggressive media. HANSA-FLEX Spain: stainless steel, corrosion-resistant and flexible.',
    },
    es: {
      title: 'Mangueras metálicas de acero inox | HANSA-FLEX España',
      description: 'Mangueras metálicas de acero inoxidable para temperaturas extremas, alta presión y medios agresivos. Resistentes a corrosión. HANSA-FLEX España.',
    },
  },
  industrial_hoses: {
    en: {
      title: 'Industrial hoses | HANSA-FLEX Spain – Food, chemical & water',
      description: 'Industrial hoses for food, chemical, water, fuel and abrasive applications. HANSA-FLEX Spain: complete solutions with full technical support.',
    },
    es: {
      title: 'Mangueras industriales | HANSA-FLEX España - Alimentos, productos químicos y agua',
      description: 'Mangueras industriales para alimentación, química, agua, combustible y aplicaciones abrasivas. HANSA-FLEX España ofrece soluciones completas con soporte técnico.',
    },
  },
  performed_hoses: {
    en: {
      title: 'Preformed hoses | HANSA-FLEX Spain – Custom shapes',
      description: 'Custom-shaped hydraulic hoses for tight spaces and compact systems. HANSA-FLEX Spain: preformed hoses from a single unit, to your exact geometry.',
    },
    es: {
      title: 'Mangueras preformadas | HANSA-FLEX España – Magueras preformadas o a medida',
      description: 'Mangueras hidráulicas preformadas a tu geometría exacta. Ideales para espacios reducidos y sistemas compactos. HANSA-FLEX España desde una unidad.',
    },
  },
  ext_high_pfm_hoses: {
    en: {
      title: 'High pressure hoses | HANSA-FLEX Spain – Up to 700 bar',
      description: 'Hydraulic hoses up to 700 bar for extreme high-pressure applications. HANSA-FLEX Spain supplies certified solutions for heavy-duty hydraulics.',
    },
    es: {
      title: 'Mangueras alta presión 700 bar | HANSA-FLEX España',
      description: 'Mangueras hidráulicas hasta 700 bar para aplicaciones de alta presión extrema. HANSA-FLEX España ofrece soluciones certificadas para hidráulica industrial exigente.',
    },
  },
  ptfe_hoses: {
    en: {
      title: 'PTFE hoses | HANSA-FLEX Spain – Chemical & food resistant',
      description: 'PTFE hoses for chemical, pharmaceutical and food-grade use. HANSA-FLEX Spain supplies smooth and corrugated hoses with pressure and temperature resistance.',
    },
    es: {
      title: 'Mangueras PTFE | HANSA-FLEX España – Resistentes a químicos',
      description: 'Mangueras PTFE para uso químico, farmacéutico y alimentario. HANSA-FLEX España ofrece versiones lisas y corrugadas resistentes a presión y temperatura.',
    },
  },
  hose_accessories: {
    en: {
      title: 'Hose accessories & protection | HANSA-FLEX Spain',
      description: 'Complete range of hose accessories and protection systems: sleeves, heat shields, clamps, end caps and safety whip checks for hydraulic lines.',
    },
    es: {
      title: 'Accesorios y protección de mangueras | HANSA-FLEX España',
      description: 'Gama completa de accesorios y sistemas de protección para mangueras: fundas, protección térmica, abrazaderas, tapones y seguridad antilatigazo.',
    },
  },
  pipelines: {
    en: {
      title: 'Hydraulic pipeline systems | HANSA-FLEX Spain',
      description: 'Complete hydraulic pipeline solutions: bent tubes, expansion joints, pipe fittings and end processing. HANSA-FLEX Spain delivers precision-made systems from stock.',
    },
    es: {
      title: 'Sistemas de tuberías hidráulicas | HANSA-FLEX España',
      description: 'Soluciones completas en tuberías hidráulicas: tubos curvados, compensadores, accesorios de tubería y mecanizado de extremos con fabricación de precisión.',
    },
  },
  pipebends: {
    en: {
      title: 'Pipe bending | HANSA-FLEX Spain – CNC precision bends',
      description: 'CNC precision pipe bending for hydraulic systems. HANSA-FLEX Spain supplies custom bent tubes in steel, stainless steel and aluminium from a single piece.',
    },
    es: {
      title: 'Curvas de tubería | HANSA-FLEX España – Doblado CNC',
      description: 'Curvado CNC de precisión para sistemas hidráulicos. HANSA-FLEX España suministra tubos curvados a medida en acero, acero inoxidable y aluminio desde una unidad.',
    },
  },
  pipeend_processing: {
    en: {
      title: 'Pipe-end processing | HANSA-FLEX Spain – Fittings & forming',
      description: 'Pipe-end processing for hydraulic systems: cutting rings, flaring, threading and special fittings made to your exact specifications.',
    },
    es: {
      title: 'Mecanizado de extremos de tubo | HANSA-FLEX España',
      description: 'Procesado de extremos de tubería: anillos cortantes, rebordes, roscado y racores especiales. Fabricado según tu especificación. HANSA-FLEX España.',
    },
  },
  expansion_joints: {
    en: {
      title: 'Expansion joints | HANSA-FLEX Spain – Pipeline compensators',
      description: 'Elastomeric and metal expansion joints for hydraulic pipeline systems. HANSA-FLEX Spain helps absorb thermal expansion and vibration safely.',
    },
    es: {
      title: 'Compensadores y juntas de expansión | HANSA-FLEX España',
      description: 'Juntas de expansión y compensadores para tuberías seguras. HANSA-FLEX España: elastoméricas y metálicas, absorben dilatación térmica y vibración.',
    },
  },
  sealing_tech: {
    en: {
      title: 'Hydraulic seals & sealing technology | HANSA-FLEX Spain',
      description: 'More than 11,000 sealing solutions for hydraulic systems: O-rings, rod seals, piston seals, rotary seals and custom sealing kits.',
    },
    es: {
      title: 'Sellado y estanqueidad hidráulica | HANSA-FLEX España',
      description: 'Más de 11.000 soluciones de sellado para sistemas hidráulicos: juntas tóricas, retenes, juntas émbolo y rotativas. HANSA-FLEX España.',
    },
  },
  filtration: {
    en: {
      title: 'Hydraulic filtration systems | HANSA-FLEX Spain',
      description: 'Hydraulic filtration for long system life: suction filters, return filters, high-pressure filters and bypass filtration units. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Filtración hidráulica | HANSA-FLEX España',
      description: 'Más de 11.000 soluciones de sellado para sistemas hidráulicos: juntas tóricas, juntas de vástago, juntas de pistón, juntas rotativas y kits a medida.',
    },
  },
  pneumatics: {
    en: {
      title: 'Pneumatic systems & components | HANSA-FLEX Spain',
      description: 'More than 18,000 pneumatic components for industrial systems: cylinders, valves, fittings and compressed air equipment. HANSA-FLEX Spain for complete industrial pneumatic systems.',
    },
    es: {
      title: 'Sistemas neumáticos | HANSA-FLEX España',
      description: 'Más de 18.000 componentes neumáticos: cilindros, válvulas, racores y equipos de aire comprimido. Sistemas industriales completos. HANSA-FLEX España.',
    },
  },
  technical_information: {
    en: {
      title: 'Technical information & product data | HANSA-FLEX Spain',
      description: 'Access technical data sheets, installation guides and product specifications for HANSA-FLEX hydraulic and fluid technology products in Spain.',
    },
    es: {
      title: 'Información técnica hidráulica | HANSA-FLEX España',
      description: 'Accede a fichas técnicas, guías de instalación y especificaciones de productos hidráulicos para soluciones hidráulicas y tecnología de fluidos de HANSA-FLEX en España.',
    },
  },
  approvals: {
    en: {
      title: 'Hydraulic National & international approvals | HANSA-FLEX Spain',
      description: 'National and international approvals for hydraulic products: railway, marine, oil & gas. HANSA-FLEX Spain: certified components for regulated industries.',
    },
    es: {
      title: 'Homologaciones hidráulicas | HANSA-FLEX España',
      description: 'Homologaciones nacionales e internacionales para productos hidráulicos en sectores ferroviario, marítimo y oil & gas. HANSA-FLEX España ofrece componentes certificados.',
    },
  },
  catalogues: {
    en: {
      title: 'Hydraulic product catalogues | HANSA-FLEX Spain – PDF',
      description: 'Download HANSA-FLEX product catalogues in PDF: hoses, fittings, valves, cylinders and the complete fluid technology range for engineers.',
    },
    es: {
      title: 'Catálogos hidráulicos en PDF | HANSA-FLEX España',
      description: 'Descarga los catálogos de productos HANSA-FLEX en PDF: mangueras, racores, válvulas, cilindros y soluciones de tecnología de fluidos.',
    },
  },
  hydraulic_connection_technology: {
    en: {
      title: 'Hydraulic connection technology | HANSA-FLEX Spain',
      description: 'Hydraulic connection technology: hose fittings, pipe fittings, adapters, couplings, ball valves and flanges from HANSA-FLEX Spain.',
    },
    es: {
      title: 'Tecnología de conexión hidráulica | HANSA-FLEX España',
      description: 'Tecnología de conexión hidráulica completa: racores, adaptadores, acoplamientos y válvulas de bola. Todo en una fuente. HANSA-FLEX España.',
    },
  },
  hose_fitting: {
    en: {
      title: 'Hose fittings | HANSA-FLEX Spain – Hydraulic hose connectors',
      description: 'Hydraulic hose fittings for every pressure class: crimp, interlock, reusable and push-on fittings manufactured by HANSA-FLEX Spain.',
    },
    es: {
      title: 'Racores de manguera hidráulica | HANSA-FLEX España',
      description: 'Racores para mangueras hidráulicas de todas las clases de presión: prensables, interlock, reutilizables y push-on de HANSA-FLEX España.',
    },
  },
  pipe_fitting: {
    en: {
      title: 'Hydraulic pipe fittings | HANSA-FLEX Spain – DIN & ISO',
      description: 'Hydraulic pipe fittings to DIN EN ISO 8434-1 and DIN 2353: cutting ring, flare, threaded and flanged types for all tube sizes. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Racores para tubos hidráulicos | HANSA-FLEX España',
      description: 'Racores de tubería hidráulica según DIN EN ISO 8434-1: anillo cortante, abocardado, roscados y bridados para todos los diámetros. HANSA-FLEX España.',
    },
  },
  adapters: {
    en: {
      title: 'Hydraulic adapters | HANSA-FLEX Spain – All thread types',
      description: 'Hydraulic adapters for BSP, JIC, ORFS, metric and SAE thread connections. HANSA-FLEX Spain supplies reliable solutions for leak-free systems.',
    },
    es: {
      title: 'Adaptadores hidráulicos | HANSA-FLEX España',
      description: 'Adaptadores hidráulicos para conversión entre roscas BSP, JIC, ORFS, métrica y SAE. HANSA-FLEX España: conexiones estancas para todos los sistemas.',
    },
  },
  coupling: {
    en: {
      title: 'Hydraulic couplings | HANSA-FLEX Spain – Quick-release',
      description: 'Hydraulic couplings for quick, leak-free connections: flat-face, poppet, screw and plug-in couplings for a wide range of hydraulic systems.',
    },
    es: {
      title: 'Acoplamientos hidráulicos | HANSA-FLEX España',
      description: 'Acoplamientos hidráulicos para conexiones rápidas y sin fugas: enchufes de cara plana, válvula, roscados y plug-in para sistemas hidráulicos.',
    },
  },
  ball_valves: {
    en: {
      title: 'Hydraulic ball valves | HANSA-FLEX Spain – High-pressure',
      description: 'Hydraulic ball valves for high-pressure systems: 2-way, 3-way and multi-way versions for shut-off, flow control and diverting applications.',
    },
    es: {
      title: 'Válvulas de bola hidráulicas | HANSA-FLEX España',
      description: 'Válvulas de bola hidráulicas para sistemas de alta presión: versiones de 2 vías, 3 vías y multivía para cierre, control y desvío de caudal.',
    },
  },
  flanges: {
    en: {
      title: 'Hydraulic flanges | HANSA-FLEX Spain – SAE & ISO 6164',
      description: 'Hydraulic flanges for large-bore, high-pressure connections: SAE, ISO 6164 and CETOP types for reliable hydraulic systems.',
    },
    es: {
      title: 'Bridas hidráulicas | HANSA-FLEX España – SAE e ISO 6164',
      description: 'Bridas hidráulicas para conexiones de gran diámetro y alta presión: SAE, ISO 6164, CETOP y para bombas. HANSA-FLEX España.',
    },
  },
  pumps: {
    en: {
      title: 'Hydraulic pumps | HANSA-FLEX Spain – Gear & piston pumps',
      description: 'Wide range of hydraulic pumps: gear pumps, axial and radial piston pumps, vane pumps for mobile and industrial hydraulics. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Bombas hidráulicas | HANSA-FLEX España',
      description: 'Amplia gama de bombas hidráulicas para aplicaciones móviles e industriales: bombas de engranajes, pistones axiales, pistones radiales y paletas.',
    },
  },
  drive_and_control: {
    en: {
      title: 'Hydraulic drive & control technology | HANSA-FLEX Spain',
      description: 'Hydraulic drive and control technology for complete systems: pumps, motors, cylinders, valves and sensors from HANSA-FLEX Spain.',
    },
    es: {
      title: 'Sistemas de mando hidráulico: Accionamiento y control hidráulico | HANSA-FLEX España',
      description: 'Tecnología de accionamiento y control hidráulico para sistemas completos: bombas, motores, cilindros, válvulas y sensores de HANSA-FLEX España.',
    },
  },
  hydraulic_motor: {
    en: {
      title: 'Hydraulic motors | HANSA-FLEX Spain – Orbital & geroller',
      description: 'High-performance hydraulic motors for mobile and industrial applications: orbital, geroller, gerotor and planetary motors from HANSA-FLEX Spain.',
    },
    es: {
      title: 'Motores hidráulicos | HANSA-FLEX España – Orbitales',
      description: 'Motores hidráulicos de alto rendimiento para aplicaciones móviles e industriales: motores orbitales, geroller, gerotor y planetarios de HANSA-FLEX España.',
    },
  },
  cylinder: {
    en: {
      title: 'Hydraulic cylinders | HANSA-FLEX Spain',
      description: 'Hydraulic cylinders for every application: standard, telescopic, single and double-acting. Custom-made cylinders also available. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Cilindros hidráulicos | HANSA-FLEX España',
      description: 'Cilindros hidráulicos para cada aplicación: estándar, telescópicos, simple y doble efecto. HANSA-FLEX España también fabrica a medida.',
    },
  },
  switches_sensors: {
    en: {
      title: 'Hydraulic switches & sensors | HANSA-FLEX Spain',
      description: 'Pressure switches, temperature sensors, level sensors and flow transmitters for hydraulic systems. HANSA-FLEX Spain – monitor and control your hydraulics.',
    },
    es: {
      title: 'Interruptores y sensores hidráulicos | HANSA-FLEX España',
      description: 'Interruptores de presión, sensores de temperatura, nivel y transmisores de caudal para sistemas hidráulicos. HANSA-FLEX España.',
    },
  },
  measuring_tech: {
    en: {
      title: 'Hydraulic measurement technology | HANSA-FLEX Spain',
      description: 'Hydraulic measuring instruments: pressure gauges, flow meters, thermometers and diagnostic systems. HANSA-FLEX Spain for precise system monitoring.',
    },
    es: {
      title: 'Instrumentación y medición hidráulica | HANSA-FLEX España',
      description: 'Instrumentos de medición hidráulica para una monitorización precisamanómetros, caudalímetros, termómetros y sistemas de diagnóstico. HANSA-FLEX España.',
    },
  },
  seven_hundred_hydraulics: {
    en: {
      title: '700 bar hydraulics | HANSA-FLEX Spain – Ultra-high pressure',
      description: '700 bar hydraulic hoses, fittings and valves for heavy-load and offshore applications. HANSA-FLEX Spain: ultra-high pressure components certified.',
    },
    es: {
      title: 'Hidráulica 700 bar | HANSA-FLEX España – Ultra alta presión',
      description: 'Componentes hidráulicos a 700 bar: mangueras, racores y válvulas para cargas pesadas y entornos offshore. HANSA-FLEX España.',
    },
  },
  pressure_accumulators: {
    en: {
      title: 'Hydraulic pressure accumulators | HANSA-FLEX Spain',
      description: 'Gas-charged hydraulic accumulators for energy storage, pressure stabilisation and pulsation damping: bladder, diaphragm and piston types.',
    },
    es: {
      title: 'Acumuladores de presión hidráulicos | HANSA-FLEX España',
      description: 'Acumuladores hidráulicos cargados con gas para almacenamiento de energía, estabilización de presión y amortiguación de pulsaciones.',
    },
  },
  valve_tech: {
    en: {
      title: 'Hydraulic valves technology | HANSA-FLEX Spain',
      description: 'Complete range of hydraulic valves for control, safety and efficiency: directional control, pressure relief, check, flow control and proportional valves',
    },
    es: {
      title: 'Válvulas hidráulicas | HANSA-FLEX España',
      description: 'Gama completa de válvulas hidráulicas para control, seguridad y eficiencia: direccionales, limitadoras de presión, antirretorno, de caudal y proporcionales.',
    },
  },
  pipeline_installation_valve: {
    en: {
      title: 'Pipeline installation valves | HANSA-FLEX Spain',
      description: 'Compact hydraulic valves for pipeline installation: shut-off, pressure control and directional valves for space-saving hydraulic circuit design.',
    },
    es: {
      title: 'Válvulas hidráulicas en línea | HANSA-FLEX España',
      description: 'Válvulas hidráulicas compactas para montaje en línea y diseño de circuitos optimizado: cierre, control de presión y válvulas direccionales.',
    },
  },
  cartridge_valve: {
    en: {
      title: 'Cartridge valves & valve blocks | HANSA-FLEX Spain',
      description: 'Screw-in cartridge valves and valve blocks for compact, modular hydraulic systems. HANSA-FLEX Spain: directional, pressure and flow cartridges.',
    },
    es: {
      title: 'Válvulas de cartucho | HANSA-FLEX España – Roscadas',
      description: 'Válvulas de cartucho roscadas y bloques de válvulas para sistemas hidráulicos compactos y modulares: control direccional, presión y caudal.',
    },
  },
  cetop_valve: {
    en: {
      title: 'CETOP valves | HANSA-FLEX Spain – Plate mounting',
      description: 'CETOP valves for complex hydraulic control: directional, proportional and stacked types to CETOP 3, 5 and 7 standards. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Válvulas CETOP | HANSA-FLEX España – Montaje en placa',
      description: 'Válvulas CETOP para control hidráulico complejo: distribuidoras, proporcionales y apiladas según normas CETOP 3, 5 y 7. HANSA-FLEX España.',
    },
  },
  fluid_service: {
    en: {
      title: 'Fluid management service | HANSA-FLEX Spain – 360°',
      description: '360° fluid management for hydraulics: oil analysis, filtering, cleaning and condition monitoring. HANSA-FLEX Spain – reduce downtime, extend system life.',
    },
    es: {
      title: 'Servicio de fluidos hidráulicos | HANSA-FLEX España',
      description: 'Gestión integral de fluidos para sistemas hidráulicos: análisis de aceite, filtración, limpieza y monitorización para reducir paradas y alargar la vida útil.',
    },
  },
  oil_analysis: {
    en: {
      title: 'Hydraulic oil analysis | HANSA-FLEX Spain – ISO 4406',
      description: 'Hydraulic oil analysis according to ISO 4406: contamination level, particle count and wear detection to help prevent hydraulic failures.',
    },
    es: {
      title: 'Análisis de aceite hidráulico | HANSA-FLEX España',
      description: 'Análisis de aceite hidráulico según ISO 4406: nivel de contaminación, recuento de partículas y detección de desgaste para prevenir fallos.',
    },
  },
  oil_filtering_cleaning: {
    en: {
      title: 'Hydraulic oil filtering & cleaning | HANSA-FLEX Spain',
      description: 'On-site oil filtering and cleaning: flushing, bypass filtration and contamination removal. HANSA-FLEX Spain – clean oil, longer system life.',
    },
    es: {
      title: 'Filtrado y limpieza de aceite hidráulico | HANSA-FLEX España',
      description: 'Servicios de filtración y limpieza de aceite hidráulico in situ: flushing, filtración bypass y eliminación de contaminación para alargar la vida útil del sistema.',
    },
  },
  logistics_solutions: {
    en: {
      title: 'Hydraulic logistics solutions | HANSA-FLEX Spain',
      description: 'Customised logistics for hydraulic components: kanban, kitting, warehouse management and JIT delivery. HANSA-FLEX Spain – streamline your supply chain.',
    },
    es: {
      title: 'Soluciones logísticas hidráulicas | HANSA-FLEX España',
      description: 'Soluciones logísticas a medida para componentes hidráulicos: Kanban, kitting, gestión de almacén y entrega just-in-time para optimizar tu cadena de suministro.',
    },
  },
  warehouse_management: {
    en: {
      title: 'Hydraulic warehouse management | HANSA-FLEX Spain',
      description: 'HANSA-FLEX Spain manages your hydraulic spare parts warehouse: optimised inventory, digital systems and demand-driven replenishment to cut costs.',
    },
    es: {
      title: 'Gestión de almacén hidráulico | HANSA-FLEX España',
      description: 'HANSA-FLEX España gestiona tu almacén de repuestos hidráulicos: inventario optimizado, sistemas digitales y reposición bajo demanda.',
    },
  },
  kanban_delivery: {
    en: {
      title: 'Kanban delivery for hydraulic parts | HANSA-FLEX Spain',
      description: 'Kanban delivery for hydraulic components: pull-principle restocking at your line. HANSA-FLEX Spain: zero stockouts and lower inventory costs.',
    },
    es: {
      title: 'Entrega kanban | HANSA-FLEX España – Suministro JIT',
      description: 'Sistema kanban para componentes hidráulicos: reposición por demanda en tu línea de producción. HANSA-FLEX España: cero roturas de stock.',
    },
  },
  kitting: {
    en: {
      title: 'Hydraulic kitting | HANSA-FLEX Spain',
      description: 'Hydraulic kitting service: pre-assembled installation kits delivered ready to use. HANSA-FLEX Spain reduces assembly time and errors.',
    },
    es: {
      title: 'Kitting hidráulico | HANSA-FLEX España – Kits hidráulicos',
      description: 'Servicio de kitting para componentes hidráulicos y neumáticos: kits premontados listos para usar. HANSA-FLEX España reduce tiempos de montaje.',
    },
  },
  hydraulic_workshop_containers: {
    en: {
      title: 'Hydraulic workshop containers | HANSA-FLEX Spain',
      description: 'Fully equipped mobile hydraulic workshop containers for on-site hose assembly and repair. HANSA-FLEX Spain – fast, flexible on-site service.',
    },
    es: {
      title: 'Contenedores taller hidráulicos | HANSA-FLEX España',
      description: 'Contenedores de taller hidráulico equipados para montaje y reparación de mangueras in situ. HANSA-FLEX España lleva el taller a tu instalación.',
    },
  },
  works_in_works: {
    en: {
      title: 'On-site plant hydraulic services | HANSA-FLEX Spain',
      description: 'HANSA-FLEX Spain integrates a workshop in your plant for on-demand hose manufacturing and just-in-sequence delivery – reducing production downtime.',
    },
    es: {
      title: 'Servicios hidráulicos en planta | HANSA-FLEX España',
      description: 'HANSA-FLEX España integra un taller hidráulico en tus instalaciones para fabricar mangueras bajo demanda y entregar en secuencia, reduciendo paradas de producción.',
    },
  },
  assembly_installation: {
    en: {
      title: 'Hydraulic assembly & installation | HANSA-FLEX Spain',
      description: 'Professional hydraulic assembly and installation services: pipework, hose routing, component mounting and commissioning for industrial applications.',
    },
    es: {
      title: 'Montaje e instalación hidráulica | HANSA-FLEX España',
      description: 'Montaje e instalación hidráulica profesional: tendido de tuberías, encaminamiento de mangueras, montaje y puesta en marcha. HANSA-FLEX España.',
    },
  },
  modernisation_optimisation: {
    en: {
      title: 'Hydraulic modernisation & optimisation | HANSA-FLEX Spain',
      description: 'Hydraulic system modernisation: retrofitting, efficiency upgrades and component replacement. HANSA-FLEX Spain reduces energy use and downtime.',
    },
    es: {
      title: 'Modernización y optimización hidráulica | HANSA-FLEX España',
      description: 'Modernización de sistemas hidráulicos: retrofit, mejoras de eficiencia y sustitución de componentes. HANSA-FLEX España reduce consumo y paradas.',
    },
  },
  plant_project_planning: {
    en: {
      title: 'Hydraulic project planning & management | HANSA-FLEX Spain',
      description: 'Hydraulic project planning and management: system design, coordination and on-site supervision. HANSA-FLEX Spain for complex hydraulic projects end-to-end.',
    },
    es: {
      title: 'Gestión de proyectos hidráulicos | HANSA-FLEX España',
      description: 'Planificación y gestión de proyectos hidráulicos: diseño de sistemas, coordinación y gestión in situ. HANSA-FLEX España para proyectos complejos.',
    },
  },
  installation_of_pipe_hoseline: {
    en: {
      title: 'Hydraulic pipe & hose installation | HANSA-FLEX Spain',
      description: 'Professional installation of hydraulic pipe and hose systems: planning, manufacturing and on-site work. HANSA-FLEX Spain for all industries.',
    },
    es: {
      title: 'Instalación de tuberías y mangueras | HANSA-FLEX España',
      description: 'Instalación profesional de tuberías y mangueras hidráulicas: planificación, fabricación e instalación in situ. HANSA-FLEX España.',
    },
  },
  repair_and_overhaul: {
    en: {
      title: 'Hydraulic repair & overhaul | HANSA-FLEX Spain',
      description: 'Hydraulic component repair and overhaul: pumps, motors, cylinders and valves. HANSA-FLEX Spain: fast turnaround, certified repairs and genuine parts.',
    },
    es: {
      title: 'Reparación hidráulica | HANSA-FLEX España',
      description: 'Reparación y reacondicionamiento de componentes hidráulicos: bombas, motores, cilindros y válvulas, con respuesta rápida y repuestos originales.',
    },
  },
  hydraulic_cylinder_repair: {
    en: {
      title: 'Hydraulic cylinder repair | HANSA-FLEX Spain',
      description: 'Hydraulic cylinder repair: seal replacement, rod rechroming, honing and full overhaul. HANSA-FLEX Spain restores cylinders to original performance.',
    },
    es: {
      title: 'Reparación de cilindros hidráulicos | HANSA-FLEX España',
      description: 'Reparación de cilindros hidráulicos: cambio de juntas, recromado, rectificado y revisión completa. HANSA-FLEX España restaura el rendimiento original.',
    },
  },
  pressure_accumulators_systems: {
    en: {
      title: 'Pressure accumulator service | HANSA-FLEX Spain',
      description: 'Repair and maintenance of hydraulic pressure accumulators and associated systems: inspection, nitrogen charging and full overhaul.',
    },
    es: {
      title: 'Servicio de acumuladores de presión hidráulicos | HANSA-FLEX España',
      description: 'Reparación y mantenimiento de acumuladores hidráulicos y sistemas asociados: inspección, carga de nitrógeno y reacondicionamiento completo.',
    },
  },
  digital_services: {
    en: {
      title: 'Digital hydraulic services: X-CODE | HANSA-FLEX Spain',
      description: 'Digital services for hydraulic management: X-CODE hose identification, online catalogue, scanner ordering and EDI/BMEcat integration.',
    },
    es: {
      title: 'Servicios digitales: X-CODE | HANSA-FLEX España – X-CODE',
      description: 'Servicios digitales para la gestión hidráulica: identificación de mangueras con X-CODE, catálogo online, pedidos con escáner e integración EDI/BMEcat.',
    },
  },
  scanner_solutions: {
    en: {
      title: 'Scan-to-Order solutions | HANSA-FLEX Spain',
      description: 'Scan-to-Order app and Zebra scanners capture X-CODE data to simplify hydraulic parts ordering, reduce errors and speed up procurement.',
    },
    es: {
      title: 'Soluciones escáner | HANSA-FLEX España – Scan-to-Order',
      description: 'La app Scan-to-Order y los escáneres Zebra capturan datos X-CODE para simplificar pedidos de repuestos hidráulicos, reducir errores y agilizar compras.',
    },
  },
  edi_bmecat: {
    en: {
      title: 'EDI & BMEcat integration | HANSA-FLEX Spain',
      description: 'EDI and BMEcat interfaces for digital procurement of hydraulic components. HANSA-FLEX Spain integrates with your ERP for automated ordering.',
    },
    es: {
      title: 'Integración EDI y BMEcat | HANSA-FLEX España',
      description: 'Interfaces EDI y BMEcat para aprovisionamiento digital de componentes hidráulicos. HANSA-FLEX España se integra con tu ERP para pedidos automatizados.',
    },
  },
  production: {
    en: {
      title: 'Custom hydraulic production | HANSA-FLEX Spain – OEM',
      description: 'n-house hydraulic production for OEM and custom requirements: hose lines, pipelines, cylinders and assemblies manufactured to specification.',
    },
    es: {
      title: 'Producción hidráulica a medida | HANSA-FLEX España',
      description: 'Producción hidráulica propia para OEM y necesidades a medida: mangueras, tuberías, cilindros y conjuntos fabricados según especificación.',
    },
  },
  series_production: {
    en: {
      title: 'Hydraulic series production | HANSA-FLEX Spain – OEM',
      description: 'Series production of hydraulic hose lines and pipelines for OEMs, with consistent quality, large-volume capacity and just-in-time delivery',
    },
    es: {
      title: 'Producción en serie de mangueras hidráulicas | HANSA-FLEX',
      description: 'Producción en serie de mangueras y tuberías hidráulicas para OEM, con calidad constante, capacidad para grandes volúmenes y entrega just-in-time.',
    },
  },
  customised_assembly: {
    en: {
      title: 'Custom hydraulic assemblies | HANSA-FLEX Spain',
      description: 'Custom hydraulic assemblies and pre-assembled hose kits, tested and ready to install, helping reduce assembly time, errors and production complexity.',
    },
    es: {
      title: 'Montaje hidráulico a medida | HANSA-FLEX España',
      description: 'Montajes hidráulicos a medida y kits de mangueras preensamblados, probados y listos para instalar, reduciendo tiempos, errores y complejidad.',
    },
  },
  preventative_maintainance: {
    en: {
      title: 'Preventive hydraulic maintenance | HANSA-FLEX Spain',
      description: 'Preventive maintenance for hydraulic systems: hose inspection, X-CODE tracking and scheduled replacement to avoid unplanned downtime.',
    },
    es: {
      title: 'Mantenimiento hidráulico preventivo | HANSA-FLEX España',
      description: 'Mantenimiento preventivo para sistemas hidráulicos: inspección de mangueras, trazabilidad X-CODE y sustitución programada para evitar paradas imprevist',
    },
  },
  inspection: {
    en: {
      title: 'Hydraulic inspection services | HANSA-FLEX Spain',
      description: 'Certified inspection services for hydraulic systems and work equipment: hoses, pressure accumulators and lifting platforms.',
    },
    es: {
      title: 'Servicios de inspección hidráulica | HANSA-FLEX España',
      description: 'Servicios de inspección certificada para sistemas hidráulicos y equipos de trabajo: mangueras, acumuladores de presión y plataformas elevadoras.',
    },
  },
  dguv_inspection: {
    en: {
      title: 'Regulatory Hydraulic hose inspection | HANSA-FLEX Spain',
      description: 'Regulatory inspection of hydraulic hose lines for safety and compliance, with certified checks, documentation and replacement recommendations.',
    },
    es: {
      title: 'Inspección reglamentaria de mangueras hidráulicas | HANSA-FLEX España',
      description: 'Inspección reglamentaria de mangueras hidráulicas para seguridad y cumplimiento, con revisión certificada, documentación y recomendaciones de sustitución.',
    },
  },
  industrial_hose_inspection: {
    en: {
      title: 'Industrial hose inspection | HANSA-FLEX Spain',
      description: 'Certified inspection of industrial hoses for chemical, food, water and process applications. HANSA-FLEX Spain: mobile service and test reports.',
    },
    es: {
      title: 'Inspección de mangueras industriales | HANSA-FLEX España',
      description: 'Inspección certificada de mangueras industriales: químicas, alimentarias, agua y proceso. HANSA-FLEX España: servicio móvil e informes de ensayo.',
    },
  },
  lifting_platforms_loading_bridges: {
    en: {
      title: 'Lifting platform inspection | HANSA-FLEX Spain',
      description: 'Certified inspection of lifting platforms, loading bridges, dock levellers and yard ramps, with test reports and maintenance recommendations.',
    },
    es: {
      title: 'Inspección de plataformas elevadoras | HANSA-FLEX España',
      description: 'Inspección certificada de plataformas elevadoras, puentes de carga, niveladores de muelle y rampas, con informes y recomendaciones de mantenimiento.',
    },
  },
  pressure_accumulator_inspection: {
    en: {
      title: 'Pressure accumulator inspection | HANSA-FLEX Spain',
      description: 'Certified inspection and servicing of pressure accumulators: legal compliance, nitrogen charging and test documentation. HANSA-FLEX Spain.',
    },
    es: {
      title: 'Inspección de acumuladores de presión | HANSA-FLEX España',
      description: 'Inspección certificada y servicio de acumuladores hidráulicos de presión, con revisión de cumplimiento, carga de nitrógeno y documentación de prueba.',
    },
  },
  hose_management: {
    en: {
      title: 'Hydraulic hose management | HANSA-FLEX Spain – X-CODE',
      description: '360° hose management with X-CODE: identify, track, inspect and replace hydraulic hoses on time. HANSA-FLEX Spain eliminates unplanned downtime.',
    },
    es: {
      title: 'Gestión de mangueras hidráulicas | HANSA-FLEX España',
      description: 'Gestión de mangueras 360° con X-CODE: identificar, rastrear, inspeccionar y reemplazar mangueras hidráulicas a tiempo. HANSA-FLEX España.',
    },
  },
  xcode: {
    en: {
      title: 'X-CODE hose identification system | HANSA-FLEX Spain',
      description: 'X-CODE gives every hydraulic hose a unique identifier. Scan to access hose data, order replacements and schedule inspections. HANSA-FLEX Spain.',
    },
    es: {
      title: 'X-CODE – Identificación de mangueras | HANSA-FLEX España',
      description: 'X-CODE asigna un identificador único a cada manguera: escanea para acceder a datos, pedir reemplazos y programar inspecciones. HANSA-FLEX España.',
    },
  },
  xcode_manager: {
    en: {
      title: 'X-CODE Manager | HANSA-FLEX Spain – Hose management software',
      description: 'X-CODE Manager: browser-based hose management software to track hose lines, plan inspections and order replacements. HANSA-FLEX Spain.',
    },
    es: {
      title: 'X-CODE Manager | HANSA-FLEX España – Gestor de mangueras',
      description: 'Software web para gestionar mangueras hidráulicas: seguimiento de líneas, planificación de inspecciones y pedidos de sustitución de forma rápida y fiable.',
    },
  },
  service_packages: {
    en: {
      title: 'Hose management packages | HANSA-FLEX Spain – Fixed-price',
      description: 'Fixed-price hose management packages for hydraulic systems: inspection, replacement, documentation and lifecycle control in Basic, Plus and Premium options.',
    },
    es: {
      title: 'Paquetes de gestión de mangueras | HANSA-FLEX España',
      description: 'Paquetes de gestión de mangueras a precio fijo: Basic, Plus y Premium. Inspección, sustitución y documentación de toda tu flota. HANSA-FLEX España.',
    },
  },
  engineering: {
    en: {
      title: 'Hydraulic engineering services | HANSA-FLEX Spain',
      description: 'Hydraulic engineering: system design, calculation, simulation and project management. HANSA-FLEX Spain for complex hydraulic engineering projects.',
    },
    es: {
      title: 'Servicios de ingeniería hidráulica | HANSA-FLEX España',
      description: 'Ingeniería hidráulica: diseño de sistemas, cálculo, simulación y gestión de proyectos. HANSA-FLEX España para proyectos hidráulicos complejos.',
    },
  },
  engineered_by_hansa_flex: {
    en: {
      title: 'Engineered by HANSA-FLEX | Proprietary hydraulic components',
      description: 'In-house developed hydraulic components: unique solutions not available elsewhere. HANSA-FLEX Spain – proprietary products for demanding applications.',
    },
    es: {
      title: 'Desarrollado por HANSA-FLEX | Soluciones propias',
      description: 'Componentes hidráulicos de desarrollo propio HANSA-FLEX: soluciones únicas no disponibles en otros proveedores. Para aplicaciones exigentes.',
    },
  },
  hydraulic_unit: {
    en: {
      title: 'Custom hydraulic power units | HANSA-FLEX Spain',
      description: 'Custom hydraulic power units from concept to tested unit: tanks, pumps, valves and control systems. HANSA-FLEX Spain – designed to your spec.',
    },
    es: {
      title: 'Grupos hidráulicos a medida | HANSA-FLEX España',
      description: 'Grupos hidráulicos a medida: del concepto a la unidad probada, con depósitos, bombas, válvulas y control. HANSA-FLEX España.',
    },
  },
  prototype_manufacture: {
    en: {
      title: 'Hydraulic prototype manufacturing | HANSA-FLEX Spain',
      description: 'Prototype manufacturing for custom hydraulic components: from design and development to tested prototypes built to your specifications.',
    },
    es: {
      title: 'Fabricación de prototipos hidráulicos | HANSA-FLEX España',
      description: 'Fabricación de prototipos hidráulicos a medida: del diseño al prototipo probado, rápido y según especificación. HANSA-FLEX España.',
    },
  },
  home: {
    en: {
      title: 'HANSA-FLEX Spain | Industrial hydraulics & pneumatics',
      description: 'HANSA-FLEX IBÉRICA: industrial hydraulic and pneumatic solutions, hoses, components and technical services for companies across Spain.',
      keywords: 'HANSA-FLEX Spain, industrial hydraulics Spain, pneumatic solutions, hydraulic solutions Spain, hydraulic hoses, hydraulic components, hydraulic supplier Spain, fluid technology',
    },
    es: {
      title: 'HANSA-FLEX España | Hidráulica y neumática industrial',
      description: 'HANSA-FLEX IBÉRICA: soluciones de hidráulica y neumática industrial, mangueras, componentes y servicios técnicos para empresas en España.',
      keywords: 'HANSA-FLEX España, hidráulica industrial, neumática industrial, soluciones hidráulicas, mangueras hidráulicas, latiguillos hidráulicos, componentes hidráulicos, proveedor hidráulico España, servicio hidráulico en Barcelona, soluciones hidráulicas en Madrid, servicio hidráulico en País Vasco, soluciones hidráulicas en Fuenlabrada',
    },
  },
  mobile_rapid_service: {
    en: {
      title: 'Mobile hydraulic service | HANSA-FLEX Spain',
      description: 'Cut downtime with the HANSA-FLEX mobile hydraulic service: fast on-site support for breakdowns, hoses and hydraulic components.',
      keywords: 'mobile hydraulic service, emergency hydraulic repair, on-site hydraulic service, hydraulic breakdown repair, hydraulic hose repair, hydraulic maintenance, HANSA-FLEX Spain mobile service, mobile hydraulic service in Spain',
    },
    es: {
      title: 'Servicio hidráulico móvil | HANSA-FLEX España',
      description: 'Reduce paradas con el servicio hidráulico móvil de HANSA-FLEX: asistencia rápida in situ para averías, mangueras y componentes hidráulicos.',
      keywords: 'servicio hidráulico móvil, reparación hidráulica urgente, asistencia hidráulica in situ, avería hidráulica, reparación mangueras hidráulicas, servicio técnico hidráulico, mantenimiento hidráulico, HANSA-FLEX servicio móvil, servicio hidráulico móvil en España, asistencia hidráulica in situ en España',
    },
  },
  hose_line_config: {
    en: {
      title: 'Hydraulic hose configurator | HANSA-FLEX Spain',
      description: 'Configure your hydraulic hose step by step with HANSA-FLEX: choose the hose, fittings and dimensions to match your application.',
      keywords: 'hydraulic hose configurator, hydraulic hose configuration, custom hydraulic hose, hydraulic fittings, hose connections, hose assembly, custom hose assembly, custom hydraulic hose in Spain',
    },
    es: {
      title: 'Configurador de mangueras | HANSA-FLEX España',
      description: 'Configura tu manguera hidráulica paso a paso con HANSA-FLEX: selecciona manguera, conexiones y medidas según los requisitos de tu aplicación.',
      keywords: 'configurador mangueras hidráulicas, configurar latiguillo hidráulico, manguera hidráulica a medida, conexiones hidráulicas, racores hidráulicos, montaje mangueras hidráulicas, latiguillos a medida',
    },
  },
  hydraulic_academy: {
    en: {
      title: 'Hydraulic Academy | HANSA-FLEX Spain training',
      description: 'Train with the HANSA-FLEX Hydraulic Academy: courses and seminars on hydraulics, fluid technology and hose lines for professionals.',
      keywords: 'Hydraulics Academy, hydraulic training, hydraulics courses, hydraulic systems training, fluid technology training, hydraulic seminars, HANSA-FLEX training Spain',
    },
    es: {
      title: 'Academia Hidráulica | Formación HANSA-FLEX España',
      description: 'Fórmate con la Academia Hidráulica de HANSA-FLEX: cursos y seminarios sobre hidráulica, tecnología de fluidos y conducciones para profesionales.',
      keywords: 'academia hidráulica, cursos hidráulica, formación hidráulica, curso sistemas hidráulicos, seminarios hidráulica, formación tecnología de fluidos, capacitación hidráulica, HANSA-FLEX formación en España',
    },
  },
  contact: {
    en: {
      title: 'Industrial hydraulics contact | HANSA-FLEX Spain',
      description: 'Contact HANSA-FLEX Spain for technical support, quotes and industrial hydraulics enquiries by phone, email or contact form.',
      keywords: 'contact HANSA-FLEX Spain, hydraulic support Spain, hydraulic technical support, request hydraulic quote, HANSA-FLEX contact, hydraulic services Spain',
    },
    es: {
      title: 'Contacto hidráulica industrial | HANSA-FLEX España',
      description: 'Contacta con HANSA-FLEX España para soporte técnico, presupuestos y consultas de hidráulica industrial por teléfono, email o formulario.',
      keywords: 'contacto HANSA-FLEX España, soporte técnico hidráulico, presupuesto hidráulico, asistencia técnica industrial, contacto hidráulica industrial, servicio hidráulico España, atención al cliente hidráulica',
    },
  },
  management: {
    en: {
      title: 'HANSA-FLEX Spain management | Leadership team',
      description: 'Meet the management of HANSA-FLEX Spain, part of an international group in hydraulics and fluid technology.',
      keywords: 'HANSA-FLEX management, HANSA-FLEX leadership, HANSA-FLEX board, family-owned hydraulics company, hydraulic group management, fluid technology leadership',
    },
    es: {
      title: 'Dirección HANSA-FLEX España | Equipo directivo',
      description: 'Conoce la dirección de HANSA-FLEX España con presencia internacional en hidráulica y tecnología de fluidos.',
      keywords: 'dirección HANSA-FLEX, equipo directivo HANSA-FLEX, dirección grupo HANSA-FLEX, empresa familiar hidráulica, grupo hidráulico internacional, liderazgo HANSA-FLEX',
    },
  },
  careers: {
    en: {
      title: 'Careers | Work at HANSA-FLEX Spain',
      description: 'Discover what it is like to work at HANSA-FLEX Spain: our professional areas, company culture and opportunities to develop your career.',
      keywords: 'careers at HANSA-FLEX, HANSA-FLEX jobs Spain, work in hydraulics, industrial jobs Spain, HANSA-FLEX careers, hydraulic industry careers',
    },
    es: {
      title: 'Trabaja con nosotros | Empleo HANSA-FLEX España',
      description: 'Descubre cómo es trabajar en HANSA-FLEX España y conoce nuestras áreas profesionales, cultura de empresa y oportunidades para desarrollar tu carrera.',
      keywords: 'trabajar en HANSA-FLEX, empleo HANSA-FLEX España, trabajar en hidráulica, empleo sector industrial, carreras HANSA-FLEX España, oportunidades laborales hidráulica, empleo industria España',
    },
  },
  current_vacancies: {
    en: {
      title: 'Current vacancies | Jobs at HANSA-FLEX Spain',
      description: 'Browse current vacancies at HANSA-FLEX Spain and find opportunities in hydraulics, technical service, logistics, sales and other areas.',
      keywords: 'HANSA-FLEX vacancies, HANSA-FLEX jobs, hydraulics jobs, hydraulic technician jobs, industrial jobs, technical service jobs, careers HANSA-FLEX Spain',
    },
    es: {
      title: 'Vacantes actuales | Empleo en HANSA-FLEX España',
      description: 'Consulta las vacantes actuales de HANSA-FLEX España y encuentra oportunidades profesionales en hidráulica, servicio técnico, logística, ventas y otras áreas.',
      keywords: 'vacantes HANSA-FLEX, ofertas de empleo HANSA-FLEX, empleo hidráulica, ofertas trabajo hidráulica, empleo industrial, técnico hidráulico empleo, trabajar HANSA-FLEX España',
    },
  },
  blogs: {
    en: {
      title: 'Blog & news | HANSA-FLEX Spain',
      description: 'News, success stories and technical articles from HANSA-FLEX Spain on hydraulics, maintenance, industrial applications and fluid technology.',
      keywords: 'hydraulics blog, hydraulic news, industrial hydraulics, hydraulic maintenance, fluid technology, hydraulic technical articles, hydraulic success stories, HANSA-FLEX news',
    },
    es: {
      title: 'Blog y noticias | HANSA-FLEX España',
      description: 'Descubre noticias, casos de éxito y contenidos técnicos de HANSA-FLEX España sobre hidráulica, mantenimiento, aplicaciones industriales y tecnología de fluidos.',
      keywords: 'blog hidráulica, noticias hidráulica, hidráulica industrial, consejos hidráulicos, mantenimiento hidráulico, tecnología de fluidos, casos de éxito hidráulica, HANSA-FLEX blog',
    },
  },
  blog_pedido_urgente: {
    en: {
      title: 'Moldtech success story | HANSA-FLEX Spain',
      description: 'How HANSA-FLEX Spain supported Moldtech with hydraulic solutions, product availability and a fast response to keep their operations running.',
      keywords: 'Moldtech HANSA-FLEX, industrial hydraulic solutions, hydraulics success story, hydraulic supply industry, hydraulic components industry, industrial hydraulic supplier',
    },
    es: {
      title: 'Caso de éxito Moldtech | HANSA-FLEX España',
      description: 'Descubre cómo HANSA-FLEX España apoyó a Moldtech con soluciones hidráulicas, disponibilidad de producto y respuesta rápida para mantener su operativa industrial.',
      keywords: 'Moldtech HANSA-FLEX, soluciones hidráulicas industria, caso de éxito hidráulica, suministro hidráulico industrial, componentes hidráulicos industria, proveedor hidráulico industrial, mantenimiento industrial hidráulico',
    },
  },
  innotrans2026: {
    en: {
      title: 'HANSA-FLEX at InnoTrans 2026 | Railway hydraulics',
      description: 'Visit HANSA-FLEX Spain at InnoTrans 2026, Hall 10.2 · Stand 230, and discover our hydraulic solutions for railway applications.',
      keywords: 'HANSA-FLEX InnoTrans 2026, InnoTrans hydraulics, railway hydraulics, railway hydraulic hoses, railway hydraulic components, railway solutions, EN 45545-2 hoses',
    },
    es: {
      title: 'HANSA-FLEX en InnoTrans 2026 | Hidráulica ferroviaria',
      description: 'Visita HANSA-FLEX España en InnoTrans 2026, Hall 10.2 · Stand 230, y descubre nuestras soluciones hidráulicas para aplicaciones ferroviarias.',
      keywords: 'InnoTrans 2026 hidráulica, hidráulica ferroviaria, mangueras ferroviarias, componentes hidráulicos ferroviarios, soluciones ferroviarias, mangueras EN 45545-2, HANSA-FLEX España ferroviaria',
    },
  },
  privacy_policy: {
    en: {
      title: 'Privacy policy | HANSA-FLEX Spain',
      description: 'Read the HANSA-FLEX Spain Privacy Policy and how we process, protect and use personal data.',
      keywords: 'HANSA-FLEX privacy policy, HANSA-FLEX Spain privacy, data protection HANSA-FLEX, personal data processing, GDPR HANSA-FLEX, website privacy',
    },
    es: {
      title: 'Política de privacidad | HANSA-FLEX España',
      description: 'Consulta la Política de Privacidad de HANSA-FLEX España y la información sobre el tratamiento, protección y uso de los datos personales.',
      keywords: 'política privacidad HANSA-FLEX España, privacidad HANSA-FLEX España, protección de datos HANSA-FLEX, tratamiento datos personales, RGPD HANSA-FLEX, privacidad web HANSA-FLEX España',
    },
  },
  legal_notice: {
    en: {
      title: 'Legal notice | HANSA-FLEX Spain',
      description: 'Read the HANSA-FLEX Spain Legal Notice: corporate information, terms of use and legal details relating to this website.',
      keywords: 'HANSA-FLEX legal notice, HANSA-FLEX Spain legal information, company legal information, website terms of use, corporate information HANSA-FLEX Spain',
    },
    es: {
      title: 'Aviso legal | HANSA-FLEX España',
      description: 'Consulta el Aviso Legal de HANSA-FLEX España con la información corporativa, condiciones de uso y aspectos legales relacionados con este sitio web.',
      keywords: 'aviso legal HANSA-FLEX, aviso legal HANSA-FLEX España, información legal HANSA-FLEX, datos empresa HANSA-FLEX, condiciones uso web, información corporativa HANSA-FLEX',
    },
  },
  terms_and_conditions: {
    en: {
      title: 'Terms and conditions | HANSA-FLEX Spain',
      description: 'Read the HANSA-FLEX Spain terms and conditions applying to commercial relationships, orders, supplies and services.',
      keywords: 'HANSA-FLEX Spain terms and conditions, HANSA-FLEX Spain sales terms, general terms and conditions, commercial terms, HANSA-FLEX orders, supply conditions',
    },
    es: {
      title: 'Términos y condiciones | HANSA-FLEX España',
      description: 'Consulta los términos y condiciones de HANSA-FLEX España aplicables a las relaciones comerciales, pedidos, suministros y servicios.',
      keywords: 'términos y condiciones HANSA-FLEX España, condiciones generales HANSA-FLEX, condiciones de venta, condiciones comerciales hidráulica, pedidos HANSA-FLEX España, condiciones suministro de hidráulica en España',
    },
  },
  licenses: {
    en: {
      title: 'Software licences | HANSA-FLEX Spain',
      description: 'Information on licences, software and third-party components used in the digital services and website of HANSA-FLEX Spain.',
      keywords: 'HANSA-FLEX Spain licences, HANSA-FLEX software licences, open source licences, third-party software, software legal notices, HANSA-FLEX open source',
    },
    es: {
      title: 'Licencias de software | HANSA-FLEX España',
      description: 'Consulta la información sobre licencias, software y componentes de terceros utilizados en los servicios digitales y el sitio web de HANSA-FLEX España.',
      keywords: 'licencias HANSA-FLEX España, licencias software HANSA-FLEX, licencias código abierto, software terceros, avisos legales software, open source HANSA-FLEX España',
    },
  },
};

/**
 * Metadata for a page, falling back to DEFAULT_META so every page always has a
 * title and a description even before the sheet is fully loaded in.
 */
export function getPageMeta(key, lang) {
  const language = lang === 'es' ? 'es' : 'en';
  const entry = pageMeta[key] && pageMeta[key][language];
  const fallback = DEFAULT_META[language];
  return {
    title: (entry && entry.title) || fallback.title,
    description: (entry && entry.description) || fallback.description,
    // No fallback on purpose. A generic keyword list repeated across 205 URLs
    // says nothing about any of them, so pages without their own keywords
    // simply omit the tag.
    keywords: (entry && entry.keywords) || '',
    image: (entry && entry.image) || DEFAULT_OG_IMAGE[language],
  };
}
